import { addDays as addDaysDateFns, addMinutes as addMinutesDateFns, subMilliseconds } from "date-fns";
import { FormatOptionsWithTZ, getTimezoneOffset, toZonedTime } from "date-fns-tz";
import { z } from "zod";
import { formatInTimeZone } from 'date-fns-tz'
import { de } from "date-fns/locale";

export type IANATimezone = "Europe/Berlin" |
    "Europe/Lisbon" |
    "Europe/London" |
    "Europe/Paris" |
    "Europe/Athens" |
    "Europe/Moscow" |
    "Europe/Istanbul" |
    "Asia/Dubai" |
    "Asia/Kolkata" |
    "Asia/Bangkok" |
    "Asia/Singapore" |
    "Asia/Tokyo" |
    "Asia/Seoul" |
    "Australia/Sydney" |
    "Pacific/Auckland" |
    "Pacific/Honolulu" |
    "America/Anchorage" |
    "America/Los/Angeles" |
    "America/Denver" |
    "America/Chicago" |
    "America/New/York" |
    "America/Sao/Paulo" |
    "America/Buenos/Aires" |
    "Atlantic/Reykjavik" |
    "Africa/Cairo" |
    "Africa/Johannesburg";

const IANATimezones: Set<IANATimezone> = new Set([
    "Europe/Berlin",
    "Europe/Lisbon",
    "Europe/London",
    "Europe/Paris",
    "Europe/Athens",
    "Europe/Moscow",
    "Europe/Istanbul",
    "Asia/Dubai",
    "Asia/Kolkata",
    "Asia/Bangkok",
    "Asia/Singapore",
    "Asia/Tokyo",
    "Asia/Seoul",
    "Australia/Sydney",
    "Pacific/Auckland",
    "Pacific/Honolulu",
    "America/Anchorage",
    "America/Los/Angeles",
    "America/Denver",
    "America/Chicago",
    "America/New/York",
    "America/Sao/Paulo",
    "America/Buenos/Aires",
    "Atlantic/Reykjavik",
    "Africa/Cairo",
    "Africa/Johannesburg"
]);

interface TimezoneData {
    localDatetime: string; // "2024-07-15 09:00"
    zone: IANATimezone;        // "Europe/Berlin"
}

export const localDatetimeSchema = z.object({
    localDatetime: z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(?::\d{2})?$/, {
        message: "Invalid datetime format. Expected format: 'YYYY-MM-DD HH:MM[:SS]'",
    }),
    zone: z.string({
        error: "Timezone is required",
    }).refine((val) => !!val && IANATimezones.has(val as IANATimezone), {
        message: "Invalid timezone",
    }),
}).transform(({ localDatetime, zone }) => new LocalDateTime(localDatetime, zone as IANATimezone));
class LocalDateTime {
    private _utcTimestamp: number | null = null;
    private _cacheKey: string | null = null;

    constructor(
        private local: string,
        private zone: IANATimezone
    ) { }

    private getUTCTimestamp(): number {
        const currentCacheKey = `${this.local}|${this.zone}`;

        if (this._utcTimestamp !== null && this._cacheKey === currentCacheKey) {
            return this._utcTimestamp;
        }

        // Cache miss - recalculate
        const [datePart, timePart] = this.local.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute] = timePart.split(':').map(Number);

        const localDate = Date.UTC(year, month - 1, day, hour, minute);
        const timezoneOffset = getTimezoneOffset(this.zone, localDate);
        const utcTimestamp = localDate - timezoneOffset;

        // Cache the result
        this._utcTimestamp = utcTimestamp;
        this._cacheKey = currentCacheKey;

        return utcTimestamp;
    }

    /**
     * Invalidate the cache when the datetime changes
     */
    private invalidateCache(): void {
        this._utcTimestamp = null;
        this._cacheKey = null;
    }

    /**
     * 
     * @param date The date object in UTC timezone
     * @param timezone The timezone location to convert and pin the date to, e.g. "Europe/Berlin".
     * @returns 
     */
    static fromUTCDate(utcDate: Date, targetZone: IANATimezone): LocalDateTime {
        // Convert UTC moment to target timezone
        const zonedTime = toZonedTime(utcDate, targetZone);

        const year = zonedTime.getFullYear();
        const month = String(zonedTime.getMonth() + 1).padStart(2, '0');
        const day = String(zonedTime.getDate()).padStart(2, '0');
        const hour = String(zonedTime.getHours()).padStart(2, '0');
        const minute = String(zonedTime.getMinutes()).padStart(2, '0');

        const localDatetime = `${year}-${month}-${day} ${hour}:${minute}`;

        return new LocalDateTime(localDatetime, targetZone);
    }

    /**
     * Create LocalDateTime from a date that represents a local date (not UTC)
     * @param localDate Date object representing a local date (year/month/day)
     * @param timezone The target timezone
     * @param hour Optional hour (default 0)
     * @param minute Optional minute (default 0)
     * @param second Optional second (default 0)
     */
    static fromLocalDate(
        localDate: Date,
        timezone: IANATimezone,
        hour: number = 0,
        minute: number = 0,
        second: number = 0
    ): LocalDateTime {
        const year = localDate.getFullYear();
        const month = String(localDate.getMonth() + 1).padStart(2, '0');
        const day = String(localDate.getDate()).padStart(2, '0');
        const hourStr = String(hour).padStart(2, '0');
        const minuteStr = String(minute).padStart(2, '0');
        const secondStr = String(second).padStart(2, '0');

        const localDatetime = `${year}-${month}-${day} ${hourStr}:${minuteStr}:${secondStr}`;

        return new LocalDateTime(localDatetime, timezone);
    }

    static deserialize(stored: string | { localDatetime: string, zone: string }): LocalDateTime {
        if (typeof stored === 'object') {
            const { localDatetime, zone } = stored as TimezoneData;
            if (!IANATimezones.has(zone as IANATimezone)) {
                throw new Error(`Invalid timezone: ${zone}`);
            }
            return new LocalDateTime(localDatetime, zone as IANATimezone);
        }
        const { localDatetime, zone } = JSON.parse(stored) as TimezoneData;
        return new LocalDateTime(localDatetime, zone);
    }

    clone(): LocalDateTime {
        const cloned = new LocalDateTime(this.local, this.zone);
        // Copy cache if available
        if (this._utcTimestamp !== null && this._cacheKey !== null) {
            cloned._utcTimestamp = this._utcTimestamp;
            cloned._cacheKey = this._cacheKey;
        }
        return cloned;
    }

    serialize(): string {
        return JSON.stringify({
            localDatetime: this.local,
            zone: this.zone
        } as TimezoneData);
    }

    // Convert to UTC for business logic (handles DST automatically)
    toUTC(): Date {
        const [datePart, timePart] = this.local.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute] = timePart.split(':').map(Number);

        // Create date in the specific timezone
        const localDate = Date.UTC(year, month - 1, day, hour, minute);

        const timezoneOffset = getTimezoneOffset(this.zone, localDate);

        const utcDate = subMilliseconds(localDate, timezoneOffset);

        return utcDate;
    }

    toTimezonedDate(): Date {
        const [datePart, timePart] = this.local.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute] = timePart.split(':').map(Number);

        // Try adding instead of subtracting
        const utcTime = Date.UTC(year, month - 1, day, hour, minute);
        const result = new Date(utcTime);

        return result;
    }

    format(format: string, options?: FormatOptionsWithTZ): string {
        const finalOptions: FormatOptionsWithTZ = !!options ? options : { locale: de };

        const dateUtc = this.toUTC();
        return formatInTimeZone(dateUtc, this.zone, format, finalOptions);
    }

    toJson(): TimezoneData {
        return {
            localDatetime: this.local,
            zone: this.zone,
        }
    }

    toPrismaJson(): object {
        return this.toJson() as object;
    }

    toString(): string {
        return `${this.local}, ${this.zone}`;
    }

    getHours(): number {
        const timePart = this.local.split(' ')[1];
        const [hour] = timePart.split(':').map(Number);
        return hour;
    }

    getMinutes(): number {
        const timePart = this.local.split(' ')[1];
        const [, minute] = timePart.split(':').map(Number);
        return minute;
    }

    getDayOfWeek(): number {
        // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        const [datePart] = this.local.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return date.getDay();
    }

    getStartOfDay(): LocalDateTime {
        const [datePart] = this.local.split(' ');
        return new LocalDateTime(`${datePart} 00:00`, this.zone);
    }

    getEndOfDay(): LocalDateTime {
        const [datePart] = this.local.split(' ');
        return new LocalDateTime(`${datePart} 23:59`, this.zone);
    }

    setHours(hours: number): LocalDateTime {
        if (isNaN(hours) || hours < 0 || hours > 23) {
            throw new Error('Invalid hours value. Must be between 0 and 23.');
        }
        const [datePart, timePart] = this.local.split(' ');
        const [, minute] = timePart.split(':').map(Number);
        const pad = (n: number) => n.toString().padStart(2, '0');
        this.local = `${datePart} ${pad(hours)}:${pad(minute)}`;
        return this;
    }

    isSameDay(other: LocalDateTime): boolean {
        const thisDate = this.local.split(' ')[0];
        const otherDate = other.local.split(' ')[0];
        return thisDate === otherDate;
    }

    setMinutes(minutes: number): LocalDateTime {
        if (isNaN(minutes) || minutes < 0 || minutes > 59) {
            throw new Error('Invalid minutes value. Must be between 0 and 59.');
        }
        const [datePart, timePart] = this.local.split(' ');
        const [hour] = timePart.split(':').map(Number);
        const pad = (n: number) => n.toString().padStart(2, '0');
        this.local = `${datePart} ${pad(hour)}:${pad(minutes)}`;
        return this;
    }

    // Display methods
    getDisplayTime(): string {
        return this.local.split(' ')[1]; // "09:00"
    }

    getDisplayDate(): string {
        return this.local.split(' ')[0]; // "2024-07-15"
    }

    getDisplayDateTime(): string {
        return this.getDisplayDate() + " " + this.getDisplayTime();
    }

    getTimezone(): IANATimezone {
        return this.zone; // "Europe/Berlin"
    }

    changeTimezone(targetTimezone: IANATimezone): LocalDateTime {
        if (targetTimezone === this.zone) {
            return this;
        }

        const utcDate = this.toUTC();
        const localInTargetZone = toZonedTime(utcDate, targetTimezone);
        const year = localInTargetZone.getFullYear();
        const month = localInTargetZone.getMonth() + 1;
        const day = localInTargetZone.getDate();
        const hour = localInTargetZone.getHours();
        const minute = localInTargetZone.getMinutes();
        const pad = (n: number) => n.toString().padStart(2, '0');
        const localString = `${year}-${pad(month)}-${pad(day)} ${pad(hour)}:${pad(minute)}`;

        this.local = localString;
        this.zone = targetTimezone;

        return this;
    }

    /**
     * Check if this LocalDateTime equals another LocalDateTime
     */
    equals(other: LocalDateTime): boolean {
        return this.getUTCTimestamp() === other.getUTCTimestamp();
    }

    /**
     * Check if this LocalDateTime is less than another LocalDateTime
     */
    lessThan(other: LocalDateTime): boolean {
        return this.getUTCTimestamp() < other.getUTCTimestamp();
    }

    /**
     * Check if this LocalDateTime is greater than another LocalDateTime
     */
    greaterThan(other: LocalDateTime): boolean {
        return this.getUTCTimestamp() > other.getUTCTimestamp();
    }

    /**
     * Check if this LocalDateTime is less than or equal to another LocalDateTime
     */
    lessThanOrEqual(other: LocalDateTime): boolean {
        return this.getUTCTimestamp() <= other.getUTCTimestamp();
    }

    /**
     * Check if this LocalDateTime is greater than or equal to another LocalDateTime
     */
    greaterThanOrEqual(other: LocalDateTime): boolean {
        return this.getUTCTimestamp() >= other.getUTCTimestamp();
    }

    /**
     * Allow using LocalDateTime in JavaScript comparison operators.
     * This enables <, <=, >, >=, ==, ===, etc. to work as expected.
     */
    valueOf(): number {
        return this.getUTCTimestamp();
    }

    /**
     * Allow using LocalDateTime in string contexts.
     */
    toPrimitive(hint: string): number | string {
        if (hint === "number") {
            return this.getUTCTimestamp();
        }
        return this.toString();
    }

    [Symbol.toPrimitive](hint: string): number | string {
        return this.toPrimitive(hint);
    }

    /**
     * Returns a new LocalDateTime with the specified number of minutes added.
     * @param minutes Number of minutes to add (can be negative)
     */
    private _addMinutes(minutes: number): LocalDateTime {
        // Parse the local datetime string
        const [datePart, timePart] = this.local.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute] = timePart.split(':').map(Number);

        // Create a Date object that represents the local time in the timezone
        const localDate = new Date(year, month - 1, day, hour, minute);

        // Add minutes using date-fns
        const newLocalDate = addMinutesDateFns(localDate, minutes);

        // Format back to our string format
        const newYear = newLocalDate.getFullYear();
        const newMonth = String(newLocalDate.getMonth() + 1).padStart(2, '0');
        const newDay = String(newLocalDate.getDate()).padStart(2, '0');
        const newHour = String(newLocalDate.getHours()).padStart(2, '0');
        const newMinute = String(newLocalDate.getMinutes()).padStart(2, '0');

        const newLocal = `${newYear}-${newMonth}-${newDay} ${newHour}:${newMinute}`;
        return new LocalDateTime(newLocal, this.zone);
    }

    addMinutes(minutes: number): LocalDateTime {
        const result = this._addMinutes(minutes);

        // If we seem to have gone backwards in time, we're probably in a DST transition
        if ((minutes > 0 && result.lessThan(this) || (minutes < 0 && result.greaterThan(this)))) {
            // Force progression by adding the minutes in UTC space instead
            const utcDate = this.toUTC();
            const newUtcDate = addMinutesDateFns(utcDate, minutes);
            return LocalDateTime.fromUTCDate(newUtcDate, this.zone);
        }

        return result;
    }

    /**
     * Returns a new LocalDateTime with the specified number of days added.
     * @param days Number of days to add (can be negative)
     */
    addDays(days: number): LocalDateTime {
        const [datePart, timePart] = this.local.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute] = timePart.split(':').map(Number);

        const localDate = new Date(year, month - 1, day, hour, minute);
        const newLocalDate = addDaysDateFns(localDate, days);

        const newYear = newLocalDate.getFullYear();
        const newMonth = String(newLocalDate.getMonth() + 1).padStart(2, '0');
        const newDay = String(newLocalDate.getDate()).padStart(2, '0');
        const newHour = String(newLocalDate.getHours()).padStart(2, '0');
        const newMinute = String(newLocalDate.getMinutes()).padStart(2, '0');

        const newLocal = `${newYear}-${newMonth}-${newDay} ${newHour}:${newMinute}`;
        return new LocalDateTime(newLocal, this.zone);
    }
}


export default LocalDateTime;